import {mixin} from "./mixin.js";
import 'disposablestack/auto';

type CallableDisposableStackT = DisposableStack & {
    (): void;
    readonly inner: DisposableStack;
}
class DisposableStackProxyImpl implements DisposableStack {
    inner: DisposableStack;
    get disposed(): boolean {
        return this.inner.disposed;
    }
    dispose(): void {
        this.inner.dispose();
    }
    use<T extends Disposable | null | undefined>(value: T): T {
        return this.inner.use(value);
    }
    adopt<T>(value: T, onDispose: (value: T) => void): T {
        return this.inner.adopt(value, onDispose);
    }
    defer(onDispose: () => void): void {
        return this.inner.defer(onDispose);
    }
    move(): DisposableStack {
        return this.inner.move();
    }
    [Symbol.dispose](): void {
        return this.inner[Symbol.dispose]();
    }
    get [Symbol.toStringTag](): string {
        return this.inner[Symbol.toStringTag];
    }
}
const disposableStackProxyImplProps = Object.fromEntries((Object.getOwnPropertyNames(DisposableStackProxyImpl.prototype) as (string|Symbol)[]).concat(Object.getOwnPropertySymbols(DisposableStackProxyImpl.prototype)).map(n => [
    n,
    {
        value: Object.getOwnPropertyDescriptor(DisposableStackProxyImpl.prototype, n as any)!!.value,
        enumerable: false,
        configurable: false,
        writable: false,
    }
]));
/**
 * Creates a new object implementing @type CallableDisposableStackT
 */
function callableDisposableStack(): CallableDisposableStackT {
    return callableDisposableStackFrom(new DisposableStack());
}
function callableDisposableStackFrom(inner: DisposableStack): CallableDisposableStackT {
    // create the new callable
    var s = (() => {
        s.dispose();
    }) as any as CallableDisposableStackT;
    Object.defineProperties(s, {
        inner: {
            value: inner,
            enumerable: false,
            configurable: false,
            writable: false,
        },
    });
    Object.defineProperties(s, disposableStackProxyImplProps);
    return s;
}
export const CallableDisposableStack = callableDisposableStack;
export type CallableDisposableStack = CallableDisposableStackT;

const dtor_ref = Symbol('dtor_ref');
@mixin
class StackDisposable2 implements Disposable {
    static Mixin : Function
    [Symbol.dispose]() {
        this.dtor.dispose();
    }
    get dtor(): DisposableStack {
        if (!(dtor_ref in this)) this[dtor_ref] = new DisposableStack();
        return this[dtor_ref];
    }
}

const dtor_ref_compat = Symbol('dtor_ref_compat');
@mixin
class StackDisposableCompat implements Disposable {
    static Mixin : Function
    get [Symbol.dispose](): CallableDisposableStack {
        if (!(dtor_ref_compat in this)) this[dtor_ref_compat] = CallableDisposableStack();
        return this[dtor_ref_compat];
    }
    get dtor(): DisposableStack {
        if (!(dtor_ref_compat in this)) this[dtor_ref_compat] = CallableDisposableStack();
        return this[dtor_ref_compat].inner;
    }
}

export const StackDisposable = StackDisposableCompat;
export type StackDisposable = StackDisposableCompat;

export interface IStackDisposable {
    [Symbol.dispose]: CallableDisposableStack;
}